import React, { useState } from "react";
import { Heading } from "../../../design/heading";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../../design/description-list";
import { Button } from "../../../design/button";
import { Participant } from "../../../models/Participant";
import EducationForm from "../../../components/Forms/EducationForm";
import EmptyStateView from "../../../components/EmptyStateView";

interface ParticipantEducationProps {
  participant: Participant;
}

const ParticipantEducation: React.FC<ParticipantEducationProps> = ({
  participant,
}) => {
  const [showEducationForm, setShowEducationForm] = useState(false);

  const shouldShowEmptyStateView =
    !participant.education?.highSchoolCompleted &&
    !participant.education?.highestGradeCompleted &&
    !participant.education?.collegeYears &&
    !participant.education?.collegeDegree &&
    !participant.education?.gradYears &&
    !participant.education?.gradDegree &&
    !participant.education?.licenses &&
    !participant.education?.vocationalTraining;

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between">
        <Heading>Education</Heading>
        <Button
          plain
          type="button"
          onClick={() => setShowEducationForm((prev) => !prev)}
        >
          {showEducationForm ? "Hide" : "Edit Education"}
        </Button>
      </div>

      {shouldShowEmptyStateView && !showEducationForm && (
        <div className="mt-4">
          <EmptyStateView
            title="No Education Details"
            message="No education details found for this participant."
          />
        </div>
      )}

      {/* Education Details List */}
      {!showEducationForm && (
        <div className="mt-4">
          <DescriptionList>
            {participant.education?.highSchoolCompleted !== undefined && (
              <>
                <DescriptionTerm>High School Completed</DescriptionTerm>
                <DescriptionDetails>
                  {participant.education.highSchoolCompleted ? "Yes" : "No"}
                </DescriptionDetails>
              </>
            )}
            {participant.education?.highestGradeCompleted !== undefined && (
              <>
                <DescriptionTerm>Highest Grade Completed</DescriptionTerm>
                <DescriptionDetails>
                  {participant.education.highestGradeCompleted}
                </DescriptionDetails>
              </>
            )}
            {participant.education?.collegeYears !== undefined && (
              <>
                <DescriptionTerm>College Years Completed</DescriptionTerm>
                <DescriptionDetails>
                  {participant.education.collegeYears}
                </DescriptionDetails>
              </>
            )}
            {participant.education?.collegeDegree && (
              <>
                <DescriptionTerm>College Degree</DescriptionTerm>
                <DescriptionDetails>
                  {participant.education.collegeDegree}
                </DescriptionDetails>
              </>
            )}
            {participant.education?.gradYears !== undefined && (
              <>
                <DescriptionTerm>Graduate School Years</DescriptionTerm>
                <DescriptionDetails>
                  {participant.education.gradYears}
                </DescriptionDetails>
              </>
            )}
            {participant.education?.gradDegree && (
              <>
                <DescriptionTerm>Graduate Degree</DescriptionTerm>
                <DescriptionDetails>
                  {participant.education.gradDegree}
                </DescriptionDetails>
              </>
            )}
            {participant.education?.licenses &&
              participant.education.licenses.length > 0 && (
                <>
                  <DescriptionTerm>Licenses</DescriptionTerm>
                  <DescriptionDetails>
                    {participant.education.licenses.join(", ")}
                  </DescriptionDetails>
                </>
              )}
            {participant.education?.vocationalTraining &&
              participant.education.vocationalTraining.length > 0 && (
                <>
                  <DescriptionTerm>Vocational Training</DescriptionTerm>
                  <DescriptionDetails>
                    {participant.education.vocationalTraining.join(", ")}
                  </DescriptionDetails>
                </>
              )}
          </DescriptionList>
        </div>
      )}

      {/* Education Form */}
      {showEducationForm && (
        <div className="mt-6">
          <EducationForm
            participantId={participant._id}
            value={participant.education}
            onFormSubmitted={() => setShowEducationForm(false)}
          />
        </div>
      )}
    </div>
  );
};

export default ParticipantEducation;
